/**
 * Where the signed-in person can work: every account, its locations and their
 * active job counts (GET /auth/contexts). Feeds the location switcher on the
 * Departments page — the same list the mobile header's switcher shows.
 */
import { useQuery } from '@tanstack/react-query';
import type { ContextAccount } from '@turn-connect/shared';
import { adminService } from '../services/auth';
import { queryKeys } from './queryKeys';

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

/** Accounts A–Z (as the server sends them) with their locations A–Z, so a long list reads like a directory. */
export function sortContextAccounts(accounts: ContextAccount[]): ContextAccount[] {
  return [...accounts].sort(byName).map((a) => ({ ...a, locations: [...a.locations].sort(byName) }));
}

export function useWorkContexts(userId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.workContexts(userId ?? ''),
    queryFn: async () => sortContextAccounts((await adminService.listContexts()).accounts),
    enabled: enabled && !!userId,
    // Job counts are a hint for telling look-alike shops apart, not live data.
    staleTime: 60_000,
  });
}
